import { useEffect, useState } from 'react'
import { parseNip05, verifyNip05 } from '../utils/nip05.js'
import { LS_NIP05_PREFIX, NIP05_CACHE_TTL_MS } from '../utils/constants.js'

// T16: resolves whether a (pubkey, nip05) pair verifies via the
// serverless /.well-known/nostr.json proxy. UI consumes { verified }
// — one of 'verified' | 'unverified' | 'unknown' — and renders the
// green check only on 'verified'. 'unknown' and 'unverified' both
// fall back to the self-claimed ⓘ glyph; the UI doesn't need to tell
// them apart.
//
// Caching:
// - localStorage per pubkey, 7-day TTL (constants.js).
// - Cache value includes the nip05 string → a profile edit that
//   changes the handle invalidates the entry automatically on the
//   next hook pass.
// - Cache both verified and unverified outcomes so dead or slow
//   domains don't get hammered on every page load.
// - On cache hit (fresh + matching nip05), surface instantly without
//   loading state; still kick off a background revalidate.
//
// This hook can safely be mounted multiple times for the same user
// (e.g. header + account modal rendering the same profile). The
// localStorage cache dedupes most of the work; the only true
// duplication is the fetch itself, which the Vercel edge caches at
// the proxy for an hour anyway.
export function useNip05Verification({ pubkey, nip05 }) {
  const [verified, setVerified] = useState('unknown')
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (!pubkey || !nip05) {
      setVerified('unknown')
      setLoading(false)
      return
    }
    const parsed = parseNip05(nip05)
    if (!parsed) {
      setVerified('unknown')
      setLoading(false)
      return
    }

    const cacheKey = LS_NIP05_PREFIX + pubkey
    let seededFromCache = false
    try {
      const raw = localStorage.getItem(cacheKey)
      if (raw) {
        const cached = JSON.parse(raw)
        const fresh =
          typeof cached?.checked_at === 'number' &&
          Date.now() - cached.checked_at < NIP05_CACHE_TTL_MS
        if (fresh && cached.nip05 === nip05) {
          setVerified(cached.verified ? 'verified' : 'unverified')
          seededFromCache = true
        }
      }
    } catch { /* treat as cache miss */ }

    setLoading(!seededFromCache)
    const controller = new AbortController()
    ;(async () => {
      const result = await verifyNip05(nip05, pubkey, { signal: controller.signal })
      if (controller.signal.aborted) return
      setVerified(result.verified ? 'verified' : 'unverified')
      setLoading(false)
      try {
        localStorage.setItem(cacheKey, JSON.stringify({
          nip05,
          verified: !!result.verified,
          checked_at: Date.now(),
        }))
      } catch { /* localStorage quota — noop */ }
    })()
    return () => controller.abort()
  }, [pubkey, nip05])

  return { verified, loading }
}
